import { assertUnreachable } from "lib/utils/assertUnreachable";

export type RoundLoadErrorCode =
  | "not-found"
  | "forbidden"
  | "offline-unavailable"
  | "connection"
  | "generic";

export class RoundLoadError extends Error {
  code: RoundLoadErrorCode;

  constructor(code: RoundLoadErrorCode, message: string) {
    super(message);
    this.name = "RoundLoadError";
    this.code = code;
  }
}

/**
 * Classifies an iPad round-load failure that produced no server response and
 * found no usable cached round. "Offline" and "online but unreachable" are
 * different situations for the person holding the iPad: offline, the
 * download-it-first guidance is actionable; online, the same words are
 * nonsense (a real incident: a long-idle iPad woke, its first fetch lost a
 * race with a deploy, and a connected device claimed the round "isn't
 * available offline yet" with no way to retry).
 */
export function classifyUnreachableRoundLoad(online: boolean): RoundLoadError {
  return online
    ? new RoundLoadError("connection", "Couldn't reach the server")
    : new RoundLoadError(
        "offline-unavailable",
        "This round isn't available offline yet",
      );
}

/**
 * Statuses that mean the request never reached a working backend, as opposed
 * to a backend answering with a failure. A round load hitting one of these
 * should be retried rather than shown as an error: it is what a browser sees
 * when its request lands in the gap of a rolling deploy, when the dev proxy
 * cannot reach a restarting backend (Vite answers 502), or when a gateway
 * times the request out. 503 is included deliberately — this app returns it
 * for a genuinely unavailable database, which is transient in the same way.
 *
 * An enumerated set, NOT `>= 500`, and 500 is the reason: this app returns a
 * JSON 500 from its OWN handlers for deterministic failures — round-aggregate
 * does it when context creation or aggregate init throws. Those are the server
 * answering, and sweeping them in here would tell a funder to check their
 * connection over a round that will fail identically on every retry, after
 * rebuilding the aggregate three more times to find out.
 *
 * 4xx is excluded for the same reason — a 401/403/404 is the server telling us
 * something true that will not change on a retry.
 */
const UNANSWERED_STATUSES = new Set([408, 429, 502, 503, 504]);

export function isUnansweredStatus(status: number): boolean {
  return UNANSWERED_STATUSES.has(status);
}

/**
 * Whether a classified round-load failure is the "we never got an answer"
 * kind. Pairs with `isTransportError` (offlineCache) at the call site: that
 * one classifies raw errors thrown by fetch/tRPC, this one classifies the
 * RoundLoadErrors the aggregate download raises from a response status.
 */
export function isUnreachableRoundLoadError(error: unknown): boolean {
  return error instanceof RoundLoadError && error.code === "connection";
}

export function getRoundLoadErrorMessage(error: unknown): {
  title: string;
  description: string;
  /** Whether a "Try again" re-load could plausibly succeed. */
  retryable: boolean;
} {
  // Not every load path converts statuses into RoundLoadError: the
  // cache-first bootstrap call surfaces its tRPC errors raw. Classify the
  // server-sent code structurally (not via TRPCClientError instanceof — the
  // bundle can carry more than one @trpc/client copy) so a permission or
  // existence rejection never gets a futile "Try again" button.
  const trpcCode = (error as { data?: { code?: string } } | null)?.data?.code;
  if (trpcCode === "FORBIDDEN" || trpcCode === "UNAUTHORIZED") {
    error = new RoundLoadError(
      "forbidden",
      error instanceof Error ? error.message : "Access denied",
    );
  } else if (trpcCode === "NOT_FOUND") {
    error = new RoundLoadError(
      "not-found",
      error instanceof Error ? error.message : "Round not found",
    );
  }

  if (error instanceof RoundLoadError) {
    switch (error.code) {
      case "not-found":
        return {
          title: "Round not found",
          description:
            "This round may have been deleted or reset. Check the link, or choose another round from the homepage.",
          retryable: false,
        };
      case "forbidden":
        return {
          title: "You do not have access to this round",
          description:
            "Ask a round admin to add you, or switch back if you are impersonating another user.",
          retryable: false,
        };
      case "offline-unavailable":
        return {
          title: "This round isn't available offline yet",
          description:
            "Open this round once while connected to the internet, then it will be available offline on this device.",
          retryable: true,
        };
      case "connection":
        return {
          title: "Couldn't reach the server",
          description:
            "Check your connection and try again. This can also happen for a moment while the app is being updated.",
          retryable: true,
        };
      case "generic":
        return {
          title: "Failed to load round data",
          description: error.message,
          retryable: true,
        };
      default:
        assertUnreachable(error.code);
    }
  }

  return {
    title: "Failed to load round data",
    description: "Please try again.",
    retryable: true,
  };
}
